import { useState } from "react";
import { Minus, Plus, ArrowUpRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { useCart } from "@/context/CartContext";
import { api, euro, formatError } from "@/lib/api";

const FREE_SHIPPING = 60;

export const CartDrawer = () => {
  const { items, setQty, total, open, setOpen } = useCart();
  const [loading, setLoading] = useState(false);
  const progress = Math.min(100, (total / FREE_SHIPPING) * 100);

  const checkout = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        items: items.map((i) => ({ product_id: i.id, quantity: i.quantity })),
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatError(e));
      setLoading(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent data-testid="cart-drawer" data-lenis-prevent className="flex w-full flex-col border-l border-[rgba(152,191,162,0.2)] bg-[#0b120e] p-0 sm:max-w-md">
        <SheetHeader className="border-b border-[rgba(152,191,162,0.15)] p-6 text-left">
          <p className="eyebrow">Il tuo carrello</p>
          <SheetTitle className="font-display text-4xl font-900 uppercase">La tua settimana</SheetTitle>
          <SheetDescription className="sr-only">Prodotti nel carrello</SheetDescription>
        </SheetHeader>

        <div className="flex-1 space-y-4 overflow-y-auto p-6">
          {items.length === 0 && (
            <p data-testid="cart-empty-message" className="text-[#a3b1aa]">Il carrello è vuoto. Inizia a costruire la tua settimana ideale.</p>
          )}
          {items.map((i) => (
            <div key={i.id} data-testid={`cart-item-${i.id}`} className="flex gap-4 border-b border-white/5 pb-4">
              <img src={i.image} alt={i.name} className="h-20 w-20 shrink-0 object-cover" />
              <div className="flex flex-1 flex-col justify-between">
                <p className="text-sm font-semibold leading-tight">{i.name}</p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 border border-white/10 px-2 py-1">
                    <button data-testid={`cart-item-decrease-${i.id}`} onClick={() => setQty(i.id, i.quantity - 1)} aria-label="Meno"><Minus size={14} /></button>
                    <span className="font-mono text-sm" data-testid={`cart-item-qty-${i.id}`}>{i.quantity}</span>
                    <button data-testid={`cart-item-increase-${i.id}`} onClick={() => setQty(i.id, i.quantity + 1)} aria-label="Più"><Plus size={14} /></button>
                  </div>
                  <span className="font-mono text-sm text-[#c4dcca]">{euro(i.price * i.quantity)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="space-y-4 border-t border-[rgba(152,191,162,0.15)] p-6">
          <div>
            <p className="mb-2 font-mono text-xs text-[#a3b1aa]" data-testid="free-shipping-progress-text">
              {total >= FREE_SHIPPING ? "Spedizione gratuita sbloccata" : `Ancora ${euro(FREE_SHIPPING - total)} per la spedizione gratuita`}
            </p>
            <div className="h-1 bg-white/10"><div className="h-full bg-[#c4dcca] transition-[width] duration-500" style={{ width: `${progress}%` }} /></div>
          </div>
          <div className="flex items-end justify-between">
            <span className="font-mono text-xs uppercase tracking-widest text-[#a3b1aa]">Subtotale</span>
            <span data-testid="cart-subtotal" className="font-display text-4xl font-800">{euro(total)}</span>
          </div>
          <button data-testid="checkout-submit-btn" disabled={!items.length || loading} onClick={checkout}
            className="flex w-full items-center justify-between bg-[#98bfa2] px-6 py-4 font-display text-xl font-800 uppercase text-[#050807] transition-colors hover:bg-[#c4dcca] disabled:opacity-40">
            {loading ? "Reindirizzamento…" : "Vai al pagamento"}
            {loading ? <Loader2 className="animate-spin" size={20} /> : <ArrowUpRight size={22} />}
          </button>
          <p className="text-center font-mono text-[11px] text-[#a3b1aa]">Pagamento sicuro con Stripe · Modalità test</p>
        </div>
      </SheetContent>
    </Sheet>
  );
};
